import Button from '@/components/Button';
import Input from '@/components/Input';
import RowsV2 from '@/components/v2/RowsV2';
import RowV2 from '@/components/v2/RowV2';
import clsx from '@/lib/clsx';
import { DeleteIcon } from '@/lib/constants/icons';
import selectors from '@/redux/selectors/selectors.js';
import mockTypeActions from '@/screens/apps/redux/actions/mockTypeActions';
import { randomId } from '@shared/utils/randomId';
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

const MockDataTypeEntries = ({ entries = [], mockTypeId }) => {
  const userId = useSelector(selectors.userId);
  const [localEntries, setLocalEntries] = useState([]);
  const [addEntries, setAddEntries] = useState([{ id: 'add', value: '', isNew: true }]);
  const inputRefs = useRef({});

  useEffect(() => {
    const sortedEntries = [...(entries || [])].sort((a, b) => (a.seq || 0) - (b.seq || 0));
    setLocalEntries(sortedEntries);
    // Reset add entries whenever the main entries change
    setAddEntries([{ id: 'add', value: '', isNew: true }]);
  }, [JSON.stringify(entries)]);

  const handleValueChange = (e, entryId) => {
    const value = e.target.value;

    if (entryId === 'add' || entryId === 'add-new') {
      setAddEntries((prev) => prev.map((entry) => (entry.id === entryId ? { ...entry, value } : entry)));

      // If the current add entry has a value and there's no "add-new" entry yet
      if (entryId === 'add' && value && !addEntries.find((e) => e.id === 'add-new')) {
        setAddEntries((prev) => [...prev, { id: 'add-new', value: '', isNew: true }]);
      }
    } else {
      setLocalEntries((prev) => prev.map((entry) => (entry.id === entryId ? { ...entry, value } : entry)));
    }
  };

  const handleValueBlur = (entryId) => {
    // Handle add entries
    if (entryId === 'add' || entryId === 'add-new') {
      const entry = addEntries.find((e) => e.id === entryId);
      if (!entry) return;

      if (entry.value) {
        const newEntry = {
          id: randomId(12, 'all'),
          value: entry.value,
          seq: entries.length + 1,
          createdOn: new Date(),
          createdBy: userId
        };
        mockTypeActions.set('entries', [...entries, newEntry], { mockTypeId });
      }

      if (entryId === 'add-new') {
        setAddEntries((prev) => {
          const filtered = prev.filter(
            (e) =>
              (e.id !== 'add' && e.id !== 'add-new') || // Keep other entries
              (e.id === 'add-new' && !e.value) // Keep add-new only if empty
          );

          return filtered.map((e) => (e.id === 'add-new' ? { ...e, id: 'add' } : e));
        });
      }
    } else {
      // Handle regular entries
      const entry = localEntries.find((e) => e.id === entryId);
      if (!entry) return;

      if (entry.value) {
        const updatedEntries = entries.map((e) => (e.id === entryId ? { ...e, value: entry.value } : e));
        mockTypeActions.set('entries', updatedEntries, { mockTypeId });
      }
    }
  };

  const handleDeleteEntry = async (entryId) => {
    const updatedEntries = entries.filter((entry) => entry.id !== entryId);
    const resequencedEntries = updatedEntries.map((entry, index) => ({
      ...entry,
      seq: index + 1
    }));
    mockTypeActions.set('entries', resequencedEntries, { mockTypeId });
  };

  const handleSort = (active, over) => {
    const activeId = active.id;
    const overId = over.id;

    // All sorting happens only within the localEntries array (regular entries)
    // Add entries are never sortable
    const activeIndex = localEntries.findIndex((entry) => entry.id === activeId);
    const overIndex = localEntries.findIndex((entry) => entry.id === overId);

    if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
      const updatedEntries = [...localEntries];
      const [removedItem] = updatedEntries.splice(activeIndex, 1);
      updatedEntries.splice(overIndex, 0, removedItem);

      // Update the sequence numbers
      const resequencedEntries = updatedEntries.map((entry, index) => ({
        ...entry,
        seq: index + 1
      }));

      // Update the backend with the resequenced entries
      mockTypeActions.set('entries', resequencedEntries, { mockTypeId });
    }
  };

  const handleKeyPress = (e, entryId, index, isAddEntry) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      if (isAddEntry) {
        const entry = addEntries.find((e) => e.id === entryId);

        if (entry?.value) {
          // For add entries with value, create the entry first
          handleValueBlur(entryId);

          // Focus on the add row after state update
          setTimeout(() => {
            const newAddInputEl = inputRefs.current.add;
            if (newAddInputEl) {
              newAddInputEl.focus();
            }
          }, 0);
        } else {
          // For empty add entry, focus on the first add entry
          const firstAddInputEl = inputRefs.current.add;
          if (firstAddInputEl) {
            firstAddInputEl.focus();
          }
        }
      } else {
        // For regular entries, move to the next input
        const nextIndex = (index + 1) % localEntries.length;

        if (nextIndex === 0 && localEntries.length > 0) {
          // If we're at the end of regular entries, move to add entry
          const addInputEl = inputRefs.current.add;
          if (addInputEl) {
            addInputEl.focus();
          }
        } else {
          const nextInputId = localEntries[nextIndex]?.id;
          const nextInputEl = inputRefs.current[nextInputId];
          if (nextInputEl) {
            nextInputEl.focus();
          }
        }
      }
    }
  };

  return (
    <div className="mock-data-entries">
      <div className="group-header">
        <div className="group-label">Values</div>
      </div>

      {/* Regular entries with sorting enabled */}
      {localEntries?.length > 0 && (
        <RowsV2 onSort={handleSort} className="mock-data-entries-list">
          {localEntries.map((entry, index) => (
            <RowV2 key={entry.id} className="entry-row" sortable={{ id: entry.id, data: entry }}>
              <RowV2.Left>
                <div className="entry-index">{index + 1}</div>
                <Input value={entry.value} onBlur={() => handleValueBlur(entry.id)} onChange={(e) => handleValueChange(e, entry.id)} onKeyPress={(e) => handleKeyPress(e, entry.id, index, false)} ref={(el) => (inputRefs.current[entry.id] = el)} placeholder="Value" className="borderless entry-value" />
              </RowV2.Left>
              <RowV2.Right>
                <Button className="ghost destructive" icon={DeleteIcon} title="Delete" onClick={() => handleDeleteEntry(entry.id)} />
              </RowV2.Right>
            </RowV2>
          ))}
        </RowsV2>
      )}

      {/* Add entries without sorting */}
      <RowsV2 className="mock-data-entries-list add-entries">
        {addEntries.map((entry, index) => (
          <RowV2 key={entry.id} className="entry-row">
            <RowV2.Left className={clsx(index === addEntries.length - 1 && 'last')}>
              <div className="entry-index" />
              <Input value={entry.value} onBlur={() => handleValueBlur(entry.id)} onChange={(e) => handleValueChange(e, entry.id)} onKeyPress={(e) => handleKeyPress(e, entry.id, index, true)} ref={(el) => (inputRefs.current[entry.id] = el)} placeholder="Add new value..." className="borderless entry-value add-entry" />
            </RowV2.Left>
            <RowV2.Right />
          </RowV2>
        ))}
      </RowsV2>
    </div>
  );
};

export default MockDataTypeEntries;
